import React, { useState } from "react";
import { SmallTd90, FlexWrap, SmallTd190 } from "./RemnantsStyled";
import { Button, Form } from "react-bootstrap";
import { ColumnControl } from "./useColumnControl";
import SortHeaderContainer from "./SortHeader";
import { SortConfig, SortDirection } from "./useSortableData";
import { RemnantRowProps } from "./common";
import { Paginator, usePaginator } from "../shared/Paginator";
import { useFilter, NeedUpdateFilterValue } from "./useFilter";
import { Space } from "shared/ui";
import FileSaver from "file-saver";
import { getRemnantsXlsx } from "api/ProductRemnants";
import MpSyncWatermarksStrip from "./MpSyncWatermarksStrip";

const fetchXlsx = async () => {
  const response = await getRemnantsXlsx();

  if (!response.ok) {
    return;
  }
  const blobPart = await response.blob();
  const blob = new Blob([blobPart], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  FileSaver.saveAs(blob);
};

const FullRemnantsHeader = ({
  columnControl,
  toggleSort,
  setSort,
  clearSort,
  sortConfig,
  paginator,
  pageSize,
  setPageSize,
  filters,
  filteredRowCount,
}: {
  columnControl: ColumnControl;
  toggleSort: (
    key: keyof RemnantRowProps,
    opts?: { preferDescending?: boolean }
  ) => void;
  setSort: (key: keyof RemnantRowProps, direction?: SortDirection) => void;
  clearSort: () => void;
  paginator: ReturnType<typeof usePaginator>;
  pageSize: number;
  setPageSize: (v: number) => void;
  sortConfig:
    | SortConfig<
        RemnantRowProps & {
          useInCombo: boolean;
        }
      >
    | undefined;
  filters: ReturnType<typeof useFilter>;
  filteredRowCount: number;
}) => {
  const colSpanCount =
    2 +
    (columnControl.remnantsChecked ? 1 : 0) +
    (columnControl.reservedChecked ? 1 : 0) +
    (columnControl.comboDistributionChecked ? 3 : 0) +
    (columnControl.calculationChecked ? 5 : 0);
  const [pageSizeLocal, setPageSizeLocal] = useState(pageSize);
  return (
    <thead style={{ position: "sticky", top: 0 }}>
      <tr>
        <th colSpan={colSpanCount}>
          <MpSyncWatermarksStrip />
          Фильтры
          <FlexWrap
            style={{
              flexWrap: "nowrap",
              alignItems: "flex-end",
              overflowX: "auto",
              gap: 8,
            }}
          >
            <Form.Check
              type={"checkbox"}
              checked={filters.showCombo}
              onChange={(e) => filters.setShowCombo(!filters.showCombo)}
              label={`Кобмо`}
              style={{ marginRight: 8, marginBottom: 0 }}
            />
            <Form.Check
              type={"checkbox"}
              checked={filters.showSingle}
              onChange={(e) => filters.setShowSingle(!filters.showSingle)}
              label={`Единичные`}
              style={{ marginRight: 8, marginBottom: 0 }}
            />
            <Form.Group style={{ marginBottom: 0, marginRight: 8, minWidth: 60 }}>
              <Form.Label className="small mb-0">Кол-во</Form.Label>
              <Form.Control
                size="sm"
                type="number"
                value={pageSizeLocal}
                onChange={(e) => setPageSizeLocal(+e.target.value)}
                onBlur={() => setPageSize(pageSizeLocal)}
                style={{ width: 56 }}
              />
            </Form.Group>
            <Form.Group style={{ marginBottom: 0, marginRight: 8, minWidth: 100 }}>
              <Form.Label className="small mb-0">Артикул</Form.Label>
              <Form.Control
                size="sm"
                placeholder="артикулу"
                value={filters.search}
                onChange={(val) => filters.setSearch(val.target.value)}
                style={{ width: 100 }}
              />
            </Form.Group>
          </FlexWrap>
          <FlexWrap style={{ marginTop: 8, alignItems: "flex-end", gap: 8 }}>
            <Form.Group style={{ marginBottom: 0, marginRight: 8, minWidth: 72 }}>
              <Form.Label className="small mb-0">Блок. WB</Form.Label>
              <Form.Select
                size="sm"
                value={filters.blockFilterWb}
                onChange={(e) =>
                  filters.setBlockFilterWb(
                    e.target.value as "all" | "blocked" | "unblocked"
                  )
                }
              >
                <option value="all">Все</option>
                <option value="blocked">Включена</option>
                <option value="unblocked">Выключена</option>
              </Form.Select>
            </Form.Group>
            <Form.Group style={{ marginBottom: 0, marginRight: 8, minWidth: 72 }}>
              <Form.Label className="small mb-0">Блок. Ozon</Form.Label>
              <Form.Select
                size="sm"
                value={filters.blockFilterOzon}
                onChange={(e) =>
                  filters.setBlockFilterOzon(
                    e.target.value as "all" | "blocked" | "unblocked"
                  )
                }
              >
                <option value="all">Все</option>
                <option value="blocked">Включена</option>
                <option value="unblocked">Выключена</option>
              </Form.Select>
            </Form.Group>
            <Form.Group style={{ marginBottom: 0, marginRight: 8, minWidth: 120 }}>
              <Form.Label className="small mb-0">Карантин Ozon</Form.Label>
              <Form.Select
                size="sm"
                value={filters.ozonQuarantineFilter}
                onChange={(e) =>
                  filters.setOzonQuarantineFilter(
                    e.target.value as "all" | "quarantine" | "not_quarantine"
                  )
                }
              >
                <option value="all">Все</option>
                <option value="quarantine">В карантине</option>
                <option value="not_quarantine">Без карантина</option>
              </Form.Select>
            </Form.Group>
            <Form.Group style={{ marginBottom: 0, marginRight: 8, minWidth: 72 }}>
              <Form.Label className="small mb-0">Блок. Ya</Form.Label>
              <Form.Select
                size="sm"
                value={filters.blockFilterYa}
                onChange={(e) =>
                  filters.setBlockFilterYa(
                    e.target.value as "all" | "blocked" | "unblocked"
                  )
                }
              >
                <option value="all">Все</option>
                <option value="blocked">Включена</option>
                <option value="unblocked">Выключена</option>
              </Form.Select>
            </Form.Group>
            <Form.Group style={{ marginBottom: 0, marginRight: 8, minWidth: 72 }}>
              <Form.Label className="small mb-0">Блок. Sber</Form.Label>
              <Form.Select
                size="sm"
                value={filters.blockFilterSber}
                onChange={(e) =>
                  filters.setBlockFilterSber(
                    e.target.value as "all" | "blocked" | "unblocked"
                  )
                }
              >
                <option value="all">Все</option>
                <option value="blocked">Включена</option>
                <option value="unblocked">Выключена</option>
              </Form.Select>
            </Form.Group>
            <Form.Group style={{ marginBottom: 0, marginRight: 8, minWidth: 72 }}>
              <Form.Label className="small mb-0">Блок. Ali</Form.Label>
              <Form.Select
                size="sm"
                value={filters.blockFilterAli}
                onChange={(e) =>
                  filters.setBlockFilterAli(
                    e.target.value as "all" | "blocked" | "unblocked"
                  )
                }
              >
                <option value="all">Все</option>
                <option value="blocked">Включена</option>
                <option value="unblocked">Выключена</option>
              </Form.Select>
            </Form.Group>
          </FlexWrap>
          <FlexWrap
            style={{
              marginTop: 8,
              alignItems: "flex-end",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            <Form.Group style={{ marginBottom: 0, marginRight: 8, minWidth: 100 }}>
              <Form.Label className="small mb-0">Отпр. WB</Form.Label>
              <Form.Select
                size="sm"
                value={filters.needUpdateFilterWb}
                onChange={(e) =>
                  filters.setNeedUpdateFilterWb(
                    e.target.value as NeedUpdateFilterValue
                  )
                }
              >
                <option value="all">Все</option>
                <option value="need">В очереди</option>
                <option value="no">Не в очереди</option>
              </Form.Select>
            </Form.Group>
            <Form.Group style={{ marginBottom: 0, marginRight: 8, minWidth: 100 }}>
              <Form.Label className="small mb-0">Отпр. Ozon</Form.Label>
              <Form.Select
                size="sm"
                value={filters.needUpdateFilterOzon}
                onChange={(e) =>
                  filters.setNeedUpdateFilterOzon(
                    e.target.value as NeedUpdateFilterValue
                  )
                }
              >
                <option value="all">Все</option>
                <option value="need">В очереди</option>
                <option value="no">Не в очереди</option>
              </Form.Select>
            </Form.Group>
            <Form.Group style={{ marginBottom: 0, marginRight: 8, minWidth: 100 }}>
              <Form.Label className="small mb-0">Отпр. Ya</Form.Label>
              <Form.Select
                size="sm"
                value={filters.needUpdateFilterYa}
                onChange={(e) =>
                  filters.setNeedUpdateFilterYa(
                    e.target.value as NeedUpdateFilterValue
                  )
                }
              >
                <option value="all">Все</option>
                <option value="need">В очереди</option>
                <option value="no">Не в очереди</option>
              </Form.Select>
            </Form.Group>
            <Form.Group style={{ marginBottom: 0, marginRight: 8, minWidth: 100 }}>
              <Form.Label className="small mb-0">Отпр. Sber</Form.Label>
              <Form.Select
                size="sm"
                value={filters.needUpdateFilterSber}
                onChange={(e) =>
                  filters.setNeedUpdateFilterSber(
                    e.target.value as NeedUpdateFilterValue
                  )
                }
              >
                <option value="all">Все</option>
                <option value="need">В очереди</option>
                <option value="no">Не в очереди</option>
              </Form.Select>
            </Form.Group>
            <Form.Group style={{ marginBottom: 0, marginRight: 8, minWidth: 100 }}>
              <Form.Label className="small mb-0">Отпр. Ali</Form.Label>
              <Form.Select
                size="sm"
                value={filters.needUpdateFilterAli}
                onChange={(e) =>
                  filters.setNeedUpdateFilterAli(
                    e.target.value as NeedUpdateFilterValue
                  )
                }
              >
                <option value="all">Все</option>
                <option value="need">В очереди</option>
                <option value="no">Не в очереди</option>
              </Form.Select>
            </Form.Group>
            <Form.Group style={{ marginBottom: 0, marginRight: 8, minWidth: 120 }}>
              <Form.Label className="small mb-0">Сорт. по отправке</Form.Label>
              <Form.Select
                size="sm"
                value={
                  sortConfig?.key === "last_send_wb_at"
                    ? "wb"
                    : sortConfig?.key === "last_send_ozon_at"
                    ? "ozon"
                    : sortConfig?.key === "last_send_ya_at"
                    ? "ya"
                    : sortConfig?.key === "last_send_sber_at"
                    ? "sber"
                    : sortConfig?.key === "last_send_ali_at"
                    ? "ali"
                    : ""
                }
                onChange={(e) => {
                  const v = e.target.value;
                  if (!v) {
                    clearSort();
                    return;
                  }
                  const key =
                    v === "wb"
                      ? "last_send_wb_at"
                      : v === "ozon"
                      ? "last_send_ozon_at"
                      : v === "ya"
                      ? "last_send_ya_at"
                      : v === "sber"
                      ? "last_send_sber_at"
                      : "last_send_ali_at";
                  setSort(key as keyof RemnantRowProps, SortDirection.Descending);
                }}
              >
                <option value="">—</option>
                <option value="wb">WB (свежие сверху)</option>
                <option value="ozon">Ozon (свежие сверху)</option>
                <option value="ya">Яндекс (свежие сверху)</option>
                <option value="sber">Sber (свежие сверху)</option>
                <option value="ali">Ali (свежие сверху)</option>
              </Form.Select>
            </Form.Group>
            <Form.Group style={{ marginBottom: 0, marginRight: 8, minWidth: 110 }}>
              <Form.Label className="small mb-0">Фильтр давности</Form.Label>
              <Form.Select
                size="sm"
                value={filters.sendAgeMp}
                onChange={(e) =>
                  filters.setSendAgeMp(e.target.value as any)
                }
              >
                <option value="wb">по WB</option>
                <option value="ozon">по Ozon</option>
                <option value="ya">по Яндекс</option>
                <option value="sber">по Sber</option>
                <option value="ali">по Ali</option>
              </Form.Select>
            </Form.Group>
            <Form.Group style={{ marginBottom: 0, marginRight: 8, minWidth: 130 }}>
              <Form.Label className="small mb-0">Когда отправляли</Form.Label>
              <Form.Select
                size="sm"
                value={filters.sendAgeFilter}
                onChange={(e) =>
                  filters.setSendAgeFilter(e.target.value as any)
                }
              >
                <option value="all">Все</option>
                <option value="never">Никогда</option>
                <option value="hour">За последний час</option>
                <option value="day">За сутки</option>
                <option value="older">Старше суток</option>
              </Form.Select>
            </Form.Group>
          </FlexWrap>
          <FlexWrap>
            <Paginator
              items={paginator.paginatorItems}
              currentPageNumber={paginator.currentPageNumber}
              setCurrentPageNumber={paginator.setCurrentPageNumber}
              style={{ marginTop: 8, display: "inline-flex" }}
            />
            <Space />
            <Button onClick={fetchXlsx}>Скачать форму</Button>
            <span className="small text-muted ms-2">
              По фильтрам: {filteredRowCount} строк
            </span>
          </FlexWrap>
        </th>
      </tr>
      <tr>
        <th colSpan={colSpanCount}>
          Столбцы
          <FlexWrap>
            <Form.Check
              type={"checkbox"}
              checked={columnControl.remnantsChecked}
              onChange={(e) =>
                columnControl.update("remnants", !columnControl.remnantsChecked)
              }
              label={`Остаток`}
              style={{ marginRight: 16 }}
            ></Form.Check>
            <Form.Check
              type={"checkbox"}
              checked={columnControl.reservedChecked}
              onChange={(e) =>
                columnControl.update("reserved", !columnControl.reservedChecked)
              }
              label={`Резерв`}
              style={{ marginRight: 16 }}
            ></Form.Check>
            <Form.Check
              type={"checkbox"}
              checked={columnControl.comboDistributionChecked}
              onChange={(e) =>
                columnControl.update(
                  "combo_distribution",
                  !columnControl.comboDistributionChecked
                )
              }
              label={`Распределение на комбо`}
              style={{ marginRight: 16 }}
            ></Form.Check>
            <Form.Check
              type={"checkbox"}
              checked={columnControl.calculationChecked}
              onChange={(e) =>
                columnControl.update(
                  "calculation",
                  !columnControl.calculationChecked
                )
              }
              label={`Результат`}
              style={{ marginRight: 16 }}
            ></Form.Check>
          </FlexWrap>
        </th>
      </tr>
      <tr>
        <th style={{ width: 180, minWidth: 180, maxWidth: 220 }}>Товар</th>
        <th style={{ width: 180 }}>Дата/время обновления 1С</th>
        {columnControl.remnantsChecked && (
          <th style={{ width: 90 }}>Остаток</th>
        )}
        {columnControl.reservedChecked && (
          <th style={{ width: 90 }}>Всего в резерве</th>
        )}
        {columnControl.comboDistributionChecked && (
          <th colSpan={3}>Распределение на комбо</th>
        )}
        {columnControl.calculationChecked && <th colSpan={5}>Результат</th>}
      </tr>
      <tr>
        <th style={{ width: 180, minWidth: 180, maxWidth: 220 }}>
          <SortHeaderContainer
            onClick={() => {
              toggleSort("article");
            }}
            direction={
              sortConfig?.key === "article" ? sortConfig.direction : ""
            }
          />
        </th>
        <th style={{ width: 180 }}>
          <SortHeaderContainer
            onClick={() => toggleSort("last_remnant_update_at")}
            direction={
              sortConfig?.key === "last_remnant_update_at"
                ? sortConfig.direction
                : ""
            }
          />
        </th>
        {columnControl.remnantsChecked && (
          <th style={{ width: 90 }}>
            <SortHeaderContainer
              onClick={() => {
                toggleSort("remnant");
              }}
              direction={
                sortConfig?.key === "remnant" ? sortConfig.direction : ""
              }
            />
          </th>
        )}
        {columnControl.reservedChecked && (
          <th style={{ width: 90 }}>
            <SortHeaderContainer
              onClick={() => toggleSort("totalReserved")}
              direction={
                sortConfig?.key === "totalReserved" ? sortConfig.direction : ""
              }
            />
          </th>
        )}
        {columnControl.comboDistributionChecked && (
          <>
            <SmallTd90 data-column="combo" data-row="header">
              <SortHeaderContainer
                onClick={() => {
                  toggleSort("share_in_combo");
                }}
                direction={
                  sortConfig?.key === "share_in_combo"
                    ? sortConfig.direction
                    : ""
                }
              >
                на кобмо
              </SortHeaderContainer>
            </SmallTd90>
            <SmallTd90>
              <SortHeaderContainer
                onClick={() => {
                  toggleSort("useInCombo");
                }}
                direction={
                  sortConfig?.key === "useInCombo" ? sortConfig.direction : ""
                }
              >
                остаток на комбо
              </SortHeaderContainer>
            </SmallTd90>
            <SmallTd90>
              <SortHeaderContainer
                onClick={() => {
                  toggleSort("useInCombo");
                }}
                direction={
                  sortConfig?.key === "useInCombo" ? sortConfig.direction : ""
                }
              >
                остаток шт
              </SortHeaderContainer>
            </SmallTd90>
          </>
        )}
        {columnControl.calculationChecked && (
          <>
            <SmallTd190>
              <SortHeaderContainer
                onClick={() => {
                  toggleSort("calculeted_wildberries");
                }}
                direction={
                  sortConfig?.key === "calculeted_wildberries"
                    ? sortConfig.direction
                    : ""
                }
              >
                wb
              </SortHeaderContainer>
            </SmallTd190>
            <SmallTd190>
              <SortHeaderContainer
                onClick={() => {
                  toggleSort("calculeted_ozon");
                }}
                direction={
                  sortConfig?.key === "calculeted_ozon"
                    ? sortConfig.direction
                    : ""
                }
              >
                ozon
              </SortHeaderContainer>
            </SmallTd190>
            <SmallTd190>
              <SortHeaderContainer
                onClick={() => {
                  toggleSort("calculeted_ya");
                }}
                direction={
                  sortConfig?.key === "calculeted_ya"
                    ? sortConfig.direction
                    : ""
                }
              >
                ya
              </SortHeaderContainer>
            </SmallTd190>
            <SmallTd190>
              <SortHeaderContainer
                onClick={() => {
                  toggleSort("calculeted_sber");
                }}
                direction={
                  sortConfig?.key === "calculeted_sber"
                    ? sortConfig.direction
                    : ""
                }
              >
                sber
              </SortHeaderContainer>
            </SmallTd190>
            <SmallTd190>
              <SortHeaderContainer
                onClick={() => {
                  toggleSort("calculeted_ali");
                }}
                direction={
                  sortConfig?.key === "calculeted_ali"
                    ? sortConfig.direction
                    : ""
                }
              >
                ali
              </SortHeaderContainer>
            </SmallTd190>
          </>
        )}
      </tr>
    </thead>
  );
};

export default FullRemnantsHeader;
